// Pure helpers — derive routing decisions from the BE approval-state DTO.
// Kept side-effect-free so they can be reasoned about in isolation; the
// gate component and the page consume them but do not duplicate the rule.
//
// Why a helper instead of inline ternaries: the "where do we send this
// user" rule lives in exactly one place. If a future status (e.g.
// `SUSPENDED`) becomes representable, we change the rule here once and
// every consumer follows.

import type { ApprovalStateResponse, ApprovalStateStatus } from '../types';

/**
 * Operational area = the part of the app gated behind ACTIVE membership.
 * This mirrors the `canOperate` boolean produced by the BE — we do NOT
 * recompute it from `status` so the FE never disagrees with the server.
 */
export function canAccessOperationalArea(
  response: ApprovalStateResponse | undefined,
): boolean {
  if (!response) return false;
  return response.status === 'ACTIVE' && response.canOperate === true;
}

/**
 * True for any status that must land on `/approvazione`.
 * NO_MEMBERSHIP and REJECTED both render an informative blocked state on
 * the same page; PENDING_APPROVAL renders the read-only "waiting" view.
 *
 * The undefined guard exists for the boot window where the query has not
 * yet resolved — callers should handle "still loading" themselves and
 * not call this with an in-flight result. We return `false` defensively
 * so a transient undefined never triggers a navigation.
 */
export function shouldRedirectToApproval(
  response: ApprovalStateResponse | undefined,
): boolean {
  if (!response) return false;
  return !canAccessOperationalArea(response);
}

/**
 * Italian label per status — single source of truth so the page badge
 * and any future surface (e.g. a header chip) stay in sync.
 */
export const STATUS_LABEL_IT: Record<ApprovalStateStatus, string> = {
  ACTIVE: 'Operativo',
  PENDING_APPROVAL: 'In approvazione',
  REJECTED: 'Non approvata',
  NO_MEMBERSHIP: 'Non associato',
};
